import dynamic from "next/dynamic";
import { useSWRConfig } from "swr";

import { useCallback, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Entity } from "models/entity";
import { useToggle } from "usehooks-ts";

import { faTrashAlt } from "@fortawesome/free-regular-svg-icons/faTrashAlt";
import { faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons/faCloudUploadAlt";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons/faShareAlt";
import { faSync } from "@fortawesome/free-solid-svg-icons/faSync";
import { faUnlink } from "@fortawesome/free-solid-svg-icons/faUnlink";

import CircleButton from "components/CircleButton";
import ConfirmModal from "components/ConfirmModal";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { getAllEntitiesByIdUrl } from "components/WatchlistWidget/utils";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { usePriceQuotes } from "hooks/usePriceQuote";
import { useWatchlistInteractions, useWatchlists } from "hooks/useWatchlists";

const WatchlistEditModal = dynamic(
  () => import("components/WatchlistEditModal")
);

const WatchlistShareModal = dynamic(
  () => import("components/WatchlistShareModal")
);

const WatchlistBulkImportModal = dynamic(
  () => import("components/WatchlistBulkImportModal")
);

const WatchlistCTAs = ({
  watchlistId,
  onWatchlistRemoved,
  showBulkImport,
  showUnfollowCTA,
}: {
  watchlistId: string;
  onWatchlistRemoved?: (_nextWatchlist?: any) => void;
  showBulkImport?: boolean;
  showUnfollowCTA?: boolean;
}) => {
  const { mobile } = useMediaQuery();
  const { watchlists, mutate: mutatedWatchlists } = useWatchlists();
  const watchlist = useMemo(
    () => watchlists?.find((w: any) => w.id === watchlistId),
    [watchlistId, watchlists]
  );

  const { deleteWatchlist, removeSharedWatchlist } =
    useWatchlistInteractions(watchlist);

  const { refreshPriceQuotes } = usePriceQuotes();
  const [refreshWatchlistUpdates] = useGlobalState("refreshWatchlistUpdates");
  const refresh = useCallback(() => {
    refreshPriceQuotes(watchlist?.entities.map((e: Entity) => e.security));
    refreshWatchlistUpdates?.();
  }, [refreshPriceQuotes, refreshWatchlistUpdates, watchlist?.entities]);

  const [showEditWatchlistModal, toggleEditWatchlistModal] = useToggle();
  const [showShareWatchlistModal, toggleShareWatchlistModal] = useToggle();
  const [showDeleteConfirmModal, toggleDeleteConfirmModal] = useToggle();
  const [showUnfollowConfirmModal, toggleUnfollowConfirmModal] = useToggle();
  const [showBulkImportModal, toggleBulkImportModal] = useToggle();

  // TODO: move this to shared hook
  const { mutate: globalMutate } = useSWRConfig();
  const refreshEntities = useCallback(() => {
    globalMutate(getAllEntitiesByIdUrl(watchlistId));
  }, [globalMutate, watchlistId]);

  if (mobile) {
    return (
      <div className="flex space-x-[10px]">
        <div>
          <FontAwesomeIcon
            icon={faSync}
            className="hover:text-smart-teal text-intelligent-silver"
            onClick={refresh}
          />
        </div>
        {/* {watchlist?.canEdit && (
          <>
            <div>
              <FontAwesomeIcon
                icon={faPen}
                className="hover:text-smart-teal text-intelligent-silver"
                onClick={toggleEditWatchlistModal}
              />
            </div>
            {showEditWatchlistModal && (
              <WatchlistEditModal
                watchlist={watchlist}
                onClose={(updatedWatchlist) => {
                  if (updatedWatchlist) {
                    mutatedWatchlists(
                      watchlists.map((w: any) => {
                        if (w.id === updatedWatchlist.id) {
                          return { ...w, name: updatedWatchlist.name };
                        }
                        return w;
                      })
                    );
                  }
                  toggleEditWatchlistModal();
                }}
              />
            )}
          </>
        )} */}
        {/* {watchlist?.canManage && (
          <>
            <div>
              <FontAwesomeIcon
                icon={faShareAlt}
                className="hover:text-smart-teal text-intelligent-silver"
                onClick={toggleShareWatchlistModal}
              />
            </div>
            {showShareWatchlistModal && (
              <WatchlistShareModal
                watchlist={watchlist}
                onClose={toggleShareWatchlistModal}
              />
            )}
          </>
        )}
        {showBulkImport && watchlist?.canManage && (
          <>
            <div>
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className="hover:text-smart-teal text-intelligent-silver"
                onClick={toggleBulkImportModal}
              />
            </div>
            {showBulkImportModal && (
              <WatchlistBulkImportModal
                watchlist={watchlist}
                onClose={toggleBulkImportModal}
                onChange={refreshEntities}
              />
            )}
          </>
        )} */}
        {showUnfollowCTA && watchlist?.isSharedToMe && (
          <>
            <div>
              <FontAwesomeIcon
                icon={faUnlink}
                className="hover:text-bearish-red text-intelligent-silver"
                onClick={toggleUnfollowConfirmModal}
              />
            </div>
            {showUnfollowConfirmModal && (
              <ConfirmModal
                title={
                  <span>
                    Unfollow watchlist <b>{watchlist?.name}</b>?
                  </span>
                }
                onClose={() => {
                  toggleUnfollowConfirmModal();
                }}
                onConfirm={async () => {
                  onWatchlistRemoved?.(
                    watchlists.filter((w: any) => w.id !== watchlist?.id)?.[0]
                  );
                  toggleUnfollowConfirmModal();
                  await removeSharedWatchlist();
                }}
              />
            )}
          </>
        )}
        {watchlist?.canManage && (
          <>
            <div>
              <FontAwesomeIcon
                icon={faTrashAlt}
                className="hover:text-bearish-red text-intelligent-silver hidden"
                onClick={toggleDeleteConfirmModal}
              />
            </div>
            {showDeleteConfirmModal && (
              <ConfirmModal
                title={
                  <span>
                    Delete Watchlist <b>{watchlist?.name}</b>?
                  </span>
                }
                onClose={toggleDeleteConfirmModal}
                onConfirm={async () => {
                  onWatchlistRemoved?.(
                    watchlists.filter((w: any) => w.id !== watchlist?.id)?.[0]
                  );
                  toggleDeleteConfirmModal();
                  await deleteWatchlist();
                }}
                confirmButtonText="Delete"
                confirmButtonClassName="bg-bearish-red"
              >
                <p>
                  You are about to delete <strong>{watchlist?.name}</strong>.
                  This action cannot be undone.
                </p>
                {/* <p>
                    Note: This Watchlist is currently{" "}
                    <strong>
                      shared with Azeem Ahmad, DC's Chartbook, Derek Li and 2 more
                    </strong>
                    .
                  </p> */}
              </ConfirmModal>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <OverlayTrigger overlay={<Tooltip>Refresh</Tooltip>}>
        <div>
          <CircleButton
            icon={faSync}
            className="hover:text-smart-teal"
            onClick={refresh}
          />
        </div>
      </OverlayTrigger>
      {watchlist?.canEdit && (
        <>
          <OverlayTrigger overlay={<Tooltip>Rename</Tooltip>}>
            <div>
              <CircleButton
                icon={faPen}
                className="hover:text-smart-teal"
                onClick={toggleEditWatchlistModal}
              />
            </div>
          </OverlayTrigger>
          {showEditWatchlistModal && (
            <WatchlistEditModal
              watchlist={watchlist}
              onClose={(updatedWatchlist) => {
                if (updatedWatchlist) {
                  mutatedWatchlists(
                    watchlists.map((w: any) => {
                      if (w.id === updatedWatchlist.id) {
                        return { ...w, name: updatedWatchlist.name };
                      }
                      return w;
                    })
                  );
                }
                toggleEditWatchlistModal();
              }}
            />
          )}
        </>
      )}
      {watchlist?.canManage && (
        <>
          <OverlayTrigger overlay={<Tooltip>Share</Tooltip>}>
            <div>
              <CircleButton
                icon={faShareAlt}
                className="hover:text-smart-teal"
                onClick={toggleShareWatchlistModal}
              />
            </div>
          </OverlayTrigger>
          {showShareWatchlistModal && (
            <WatchlistShareModal
              watchlist={watchlist}
              onClose={toggleShareWatchlistModal}
            />
          )}
        </>
      )}
      {showBulkImport && watchlist?.canManage && (
        <>
          <OverlayTrigger overlay={<Tooltip>Bulk Import</Tooltip>}>
            <div>
              <CircleButton
                icon={faCloudUploadAlt}
                className="hover:text-smart-teal"
                onClick={toggleBulkImportModal}
              />
            </div>
          </OverlayTrigger>
          {showBulkImportModal && (
            <WatchlistBulkImportModal
              watchlist={watchlist}
              onClose={toggleBulkImportModal}
              onChange={refreshEntities}
            />
          )}
        </>
      )}
      {showUnfollowCTA && watchlist?.isSharedToMe && (
        <>
          <OverlayTrigger overlay={<Tooltip>Unfollow this watchlist</Tooltip>}>
            <div>
              <CircleButton
                icon={faUnlink}
                className="hover:text-bearish-red"
                onClick={toggleUnfollowConfirmModal}
              />
            </div>
          </OverlayTrigger>
          {showUnfollowConfirmModal && (
            <ConfirmModal
              title={
                <span>
                  Unfollow watchlist <b>{watchlist?.name}</b>?
                </span>
              }
              onClose={() => {
                toggleUnfollowConfirmModal();
              }}
              onConfirm={async () => {
                onWatchlistRemoved?.(
                  watchlists.filter((w: any) => w.id !== watchlist?.id)?.[0]
                );
                toggleUnfollowConfirmModal();
                await removeSharedWatchlist();
              }}
            />
          )}
        </>
      )}
      {watchlist?.canManage && (
        <>
          <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
            <div>
              <CircleButton
                icon={faTrashAlt}
                className="hover:text-bearish-red"
                onClick={toggleDeleteConfirmModal}
              />
            </div>
          </OverlayTrigger>
          {showDeleteConfirmModal && (
            <ConfirmModal
              title={
                <span>
                  Delete Watchlist <b>{watchlist?.name}</b>?
                </span>
              }
              onClose={toggleDeleteConfirmModal}
              onConfirm={async () => {
                onWatchlistRemoved?.(
                  watchlists.filter((w: any) => w.id !== watchlist?.id)?.[0]
                );
                toggleDeleteConfirmModal();
                await deleteWatchlist();
              }}
              confirmButtonText="Delete"
              confirmButtonClassName="bg-bearish-red"
            >
              <p>
                You are about to delete <strong>{watchlist?.name}</strong>. This
                action cannot be undone.
              </p>
              {/* <p>
                  Note: This Watchlist is currently{" "}
                  <strong>
                    shared with Azeem Ahmad, DC's Chartbook, Derek Li and 2 more
                  </strong>
                  .
                </p> */}
            </ConfirmModal>
          )}
        </>
      )}
    </>
  );
};

export default WatchlistCTAs;
